import { QueryClientProvider } from '@tanstack/react-query';
import cx from 'classnames';
import { Provider as JotaiProvider } from 'jotai';
import React from 'react';
import { RawIntlProvider } from 'react-intl';
import { LayerContext } from '@dropbox/dig-components/layer';
import { FileViewerThemeProvider } from 'js/file_viewer/components/theme_provider';
import { FileViewerContextProvider } from 'js/file_viewer/contexts/file_viewer';
import { PreviewControlProvider } from 'js/file_viewer/contexts/preview_controls';
import { activeEditorIdAtom } from 'js/file_viewer/core/atoms/editing';
import { useJotaiStore } from 'js/file_viewer/core/atoms/store/use_store';
import { isFullScreenAtom } from 'js/file_viewer/core/atoms/ui/general';
import { DeviceType } from 'js/file_viewer/core/logging/constants';
import { DEFAULT_CONFIG } from 'js/file_viewer/core/utils/config';
import { v4 } from 'js/file_viewer/core/utils/uuid';
import * as styles from 'js/file_viewer/file_viewer.module.css';
import { useLogUserActionInternal } from 'js/file_viewer/hooks/logging';
import { KeyboardBindingProvider } from 'js/file_viewer/keyboard/keyboard_provider';
import { queryClient } from 'js/state/src/query_client/query_client';
const DEFAULT_ID = 'fvsdk-mount-point';
/**
 * FileViewer is a required wrapper for all File Viewer components. It provides access to the FileViewer API, located
 * in js/file_viewer/api, for all wrapped components. It also provides the Jotai store and necessary React Contexts
 * for its children.
 */
export const FileViewer = (props) => {
    const { children, intl, featureConfig: partialFeatureConfig, loggingSessionId: externalLoggingSessionId, apiV2ClientShim, optionalIOClients, logger, className, id, isLoggedIn, sourceSession, initialEditorParams, deviceType, } = props;
    const loggingSessionId = React.useMemo(() => externalLoggingSessionId !== null && externalLoggingSessionId !== void 0 ? externalLoggingSessionId : v4(), [externalLoggingSessionId]);
    const jotaiStore = useJotaiStore({ optionalIOClients });
    const featureConfig = React.useMemo(() => ({ ...DEFAULT_CONFIG, ...partialFeatureConfig }), [partialFeatureConfig]);
    React.useEffect(() => {
        if (initialEditorParams) {
            const { activeEditPlugin, editActivationParams } = initialEditorParams;
            jotaiStore.set(activeEditorIdAtom, activeEditPlugin, editActivationParams);
        }
    }, [initialEditorParams, jotaiStore]);
    const logUserAction = useLogUserActionInternal(jotaiStore, logger);
    const sourceSessionInternal = React.useMemo(() => ({ ...sourceSession, was_flipped: false }), [sourceSession]);
    const contextValue = React.useMemo(() => {
        var _a;
        return ({
            loggingSessionId,
            featureConfig,
            canCloseViewer: !!((_a = optionalIOClients.navigationClient) === null || _a === void 0 ? void 0 : _a.closeViewer),
            isLoggedIn,
            deviceType: deviceType || DeviceType.desktop,
            sourceSession: sourceSessionInternal,
            apiV2ClientShim,
            optionalIOClients,
            logger,
            logUserAction,
        });
    }, 
    // these are not expected to actually change in the course of a preview session.
    [
        loggingSessionId,
        featureConfig,
        optionalIOClients,
        isLoggedIn,
        deviceType,
        sourceSessionInternal,
        apiV2ClientShim,
        logger,
        logUserAction,
    ]);
    const isFullScreen = jotaiStore.get(isFullScreenAtom);
    return (React.createElement("div", { className: className || styles.fvsdkMountPoint, id: id || DEFAULT_ID },
        React.createElement(QueryClientProvider, { client: queryClient },
            React.createElement(JotaiProvider, { store: jotaiStore },
                React.createElement(RawIntlProvider, { value: intl },
                    React.createElement(FileViewerContextProvider, { value: contextValue },
                        React.createElement(PreviewControlProvider, null,
                            React.createElement(KeyboardBindingProvider, { disableBindings: !featureConfig.keyboard_shortcuts },
                                React.createElement(LayerContext.Provider, { value: { zIndex: 501 } },
                                    React.createElement(FileViewerThemeProvider, null,
                                        React.createElement("div", { className: cx(styles.fileViewerArea, {
                                                [styles.fullScreenPreview]: isFullScreen,
                                            }) }, children)))))))))));
};
FileViewer.displayName = "FileViewer"
//# sourceMappingURL=file_viewer.preenvesnextjs.map
